import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';

function HeroSection() {
  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20composition%20of%20diverse%20fashion%20pieces%20arranged%20in%20dynamic%20diagonal%20sweep%20across%20warm%20cream%20surface%2C%20sneakers%20sunglasses%20chains%20caps%20and%20small%20accessories%20scattered%20artfully%2C%20soft%20natural%20window%20light%20creating%20gentle%20shadows%2C%20editorial%20product%20photography%2C%20warm%20beige%20and%20terracotta%20tones%2C%20clean%20composition%20with%20negative%20space&width=1800&height=1200&seq=hero-kakobuy-v2-2026&orientation=landscape')`,
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-r from-background-50/95 via-background-50/70 to-background-50/30"></div>

      <div className="relative w-full px-4 md:px-6 pt-20 pb-16">
        <div className="max-w-2xl">
          <span className="inline-block px-4 py-1.5 text-sm font-medium bg-accent-100/90 text-accent-800 rounded-full mb-6">
            Updated 2026
          </span>

          <p className="text-5xl md:text-7xl font-heading font-bold text-foreground-950 leading-none mb-6">
            Kakobuy
            <br />
            Spreadsheet
          </p>

          <p className="text-lg text-foreground-500 leading-relaxed max-w-md mb-10">
            Your complete resource for Kakobuy spreadsheets, product categories, comparison guides and shopping tutorials. Discover thousands of product finds faster.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 h-14 flex items-center justify-center gap-3 text-base font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer group"
            >
              Kakobuy Sheet
              <span className="w-5 h-5 rounded-full bg-background-50/20 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                <i className="ri-arrow-right-line text-xs text-background-50"></i>
              </span>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 h-14 flex items-center justify-center text-base font-semibold border-2 border-primary-500 text-primary-600 rounded-full hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="w-6 h-10 rounded-full border-2 border-foreground-300 flex items-start justify-center p-2">
          <div className="w-1 h-2.5 rounded-full bg-foreground-400 animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  return (
    <section className="w-full py-8 bg-background-100 border-y border-background-200/70">
      <div className="px-4 md:px-6">
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
          {[
            { icon: 'ri-refresh-line', text: 'Updated Resources' },
            { icon: 'ri-folder-2-line', text: 'Category Organized' },
            { icon: 'ri-user-smile-line', text: 'Beginner Friendly' },
            { icon: 'ri-shield-check-line', text: 'Safety Guide' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center text-lg text-primary-500">
                <i className={item.icon}></i>
              </div>
              <span className="text-sm font-medium text-foreground-600 whitespace-nowrap">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategorySection() {
  return (
    <section className="w-full py-20 md:py-28 bg-background-50">
      <div className="px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div className="flex items-end gap-6">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-foreground-400 mb-1">Shop by</p>
              <h2 className="text-4xl md:text-5xl font-heading font-bold italic text-primary-600">Category</h2>
            </div>
            <p className="text-sm text-foreground-500 max-w-xs leading-relaxed hidden lg:block pb-1">
              Browse our organized product categories to find exactly what you are looking for in the spreadsheet.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group block bg-background-100 rounded-lg border border-background-200/70 overflow-hidden hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={cat.imageUrl}
                  alt={cat.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-3 md:p-4">
                <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors">
                  {cat.name}
                </h3>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrendingSection() {
  const trendingItems = [
    { name: 'Chunky Platform Sneakers', category: 'Shoes', price: '¥359', imageUrl: 'https://readdy.ai/api/search-image?query=Single%20pair%20of%20chunky%20platform%20sneakers%20in%20pastel%20colorway%20on%20vibrant%20gradient%20backdrop%2C%20bold%20product%20photography%2C%2090s%20rave%20aesthetic%2C%20colorful%20yet%20clean%20composition%2C%20soft%20studio%20lighting%2C%20eye-catching%20fashion%20editorial%20style&width=600&height=480&seq=trend-platform-sneakers-v2&orientation=landscape' },
    { name: 'Cable Knit Sweater', category: 'Hoodies', price: '¥198', imageUrl: 'https://readdy.ai/api/search-image?query=Extreme%20close-up%20of%20luxurious%20cable%20knit%20cream%20sweater%20showing%20intricate%20texture%20pattern%2C%20soft%20directional%20side%20lighting%20creating%20depth%20and%20shadow%2C%20cozy%20tactile%20aesthetic%2C%20premium%20fashion%20detail%20photography%2C%20warm%20ivory%20tones&width=600&height=480&seq=trend-knit-sweater-v2&orientation=landscape' },
    { name: 'Embroidered Bomber Jacket', category: 'Jackets', price: '¥288', imageUrl: 'https://readdy.ai/api/search-image?query=Satin%20bomber%20jacket%20with%20colorful%20embroidered%20floral%20patches%20displayed%20against%20clean%20monochrome%20backdrop%2C%20contemporary%20streetwear%20aesthetic%2C%20soft%20studio%20lighting%2C%20bold%20artistic%20fashion%20photography%2C%20rich%20jewel%20tones&width=600&height=480&seq=trend-bomber-jacket-v2&orientation=landscape' },
    { name: 'Mini Leather Backpack', category: 'Bags', price: '¥129', imageUrl: 'https://readdy.ai/api/search-image?query=Mini%20leather%20backpack%20with%20brushed%20gold%20hardware%20placed%20on%20speckled%20terrazzo%20surface%2C%20soft%20natural%20side%20lighting%2C%20modern%20minimalist%20accessory%20photography%2C%20sophisticated%20everyday%20luxury%20aesthetic%2C%20warm%20neutral%20palette&width=600&height=480&seq=trend-mini-backpack-v2&orientation=landscape' },
    { name: 'Fisherman Bucket Hat', category: 'Headwear', price: '¥65', imageUrl: 'https://readdy.ai/api/search-image?query=Stylish%20fisherman%20bucket%20hat%20casting%20artistic%20geometric%20shadow%20patterns%20on%20soft%20pastel%20pink%20background%2C%20creative%20product%20photography%2C%20playful%20summer%20aesthetic%2C%20diffused%20studio%20lighting%2C%20whimsical%20and%20modern%20composition&width=600&height=480&seq=trend-bucket-hat-v2&orientation=landscape' },
  ];

  return (
    <section className="w-full py-20 md:py-28 bg-background-100">
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between mb-10">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
            Trending in Spreadsheet
          </h2>
          <span className="hidden md:inline-flex px-4 py-1.5 text-xs font-semibold bg-accent-100 text-accent-800 rounded-full">
            Live Database
          </span>
        </div>

        <div className="flex gap-5 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
          {trendingItems.map((item, idx) => (
            <a
              key={idx}
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex-shrink-0 w-[260px] md:w-[300px] bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
            >
              <div className="h-48 md:h-56 overflow-hidden">
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4">
                <p className="text-xs text-foreground-400 uppercase tracking-wide mb-1">{item.category}</p>
                <h4 className="text-sm font-medium text-foreground-800 mb-2">{item.name}</h4>
                <p className="text-lg font-bold text-foreground-950 text-right">{item.price}</p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function ArticleHub() {
  return (
    <section className="w-full py-20 md:py-28 bg-background-50">
      <div className="px-4 md:px-6">
        <div className="max-w-2xl mb-12">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-4">
            Topical Authority
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-light text-foreground-950 leading-tight mb-8">
            Learn Everything About
            <br />
            Kakobuy Spreadsheets
          </h2>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/kakobuy-spreadsheet-guide"
              className="px-6 h-11 flex items-center text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Beginner Guide
            </Link>
            <Link
              to="/kakobuy-spreadsheet"
              className="px-6 h-11 flex items-center text-sm font-semibold border-2 border-primary-500 text-primary-600 rounded-full hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
            >
              View Resource Hub
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article) => (
            <ArticleCard
              key={article.slug}
              slug={article.slug}
              title={article.title}
              excerpt={article.excerpt}
              category={article.category}
              imageUrl={article.imageUrl}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  return (
    <section className="w-full py-20 md:py-28 bg-background-100">
      <div className="px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
          <div className="w-full lg:w-2/5">
            <div className="rounded-2xl overflow-hidden aspect-[4/5]">
              <img
                src="https://readdy.ai/api/search-image?query=Close-up%20of%20hands%20holding%20magnifying%20glass%20over%20fabric%20swatches%20clothing%20labels%20and%20product%20tags%20on%20light%20oak%20desk%2C%20product%20quality%20inspection%20concept%2C%20soft%20natural%20daylight%2C%20professional%20yet%20warm%20aesthetic%2C%20clean%20composition%2C%20warm%20neutral%20tones%2C%20trust%20and%20care%20theme&width=800&height=1000&seq=trust-quality-v2-2026&orientation=portrait"
                alt="Quality inspection process"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="w-full lg:w-3/5">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">(Evaluation Method)</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-8">
              Why Kakobuy
              <br />
              <span className="text-foreground-400">Spreadsheet?</span>
            </h2>

            <blockquote className="border-l-2 border-accent-400 pl-6 mb-8">
              <p className="text-base text-foreground-600 leading-relaxed italic">
                &ldquo;We compare platforms based on information organization, user experience, accessibility, category coverage, and safety transparency. Our research methodology ensures you get accurate, unbiased information to make informed shopping decisions.&rdquo;
              </p>
            </blockquote>

            <p className="text-sm font-semibold text-foreground-800">
              — Kakobuy Finds Research Team
            </p>

            <div className="flex gap-3 mt-8">
              <Link
                to="/safety"
                className="px-6 h-11 flex items-center text-sm font-medium border-2 border-foreground-300 text-foreground-600 rounded-full hover:border-primary-400 hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Read Safety Guide
              </Link>
              <Link
                to="/about"
                className="px-6 h-11 flex items-center text-sm font-medium bg-foreground-900 text-background-50 rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
              >
                About Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative w-full py-24 md:py-32 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Panoramic%20row%20of%20twelve%20colorful%20sneakers%20arranged%20in%20gradient%20rainbow%20order%20from%20warm%20coral%20to%20cool%20mint%20on%20clean%20white%20surface%2C%20vibrant%20product%20photography%2C%20bold%20and%20energetic%20composition%2C%20soft%20even%20studio%20lighting%2C%20fashion%20retail%20display%20aesthetic&width=1800&height=800&seq=cta-full-v2-2026&orientation=landscape')`,
        }}
      ></div>
      <div className="absolute inset-0 bg-foreground-950/40"></div>

      <div className="relative w-full px-4 md:px-6">
        <div className="max-w-3xl">
          <h2 className="text-5xl md:text-7xl font-heading font-bold text-background-50 leading-none mb-8">
            kakobuy finds
            <br />
            2026
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 h-14 flex items-center justify-center gap-3 text-base font-semibold bg-background-50 text-foreground-950 rounded-full hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer group"
            >
              Explore Full Sheet
              <span className="w-5 h-5 rounded-full bg-foreground-950 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                <i className="ri-arrow-right-line text-xs text-background-50"></i>
              </span>
            </a>
            <p className="text-lg text-background-200 leading-relaxed max-w-xs">
              Access the complete spreadsheet database and start discovering products today.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function SafetyDisclaimer() {
  return (
    <section className="w-full py-12 bg-accent-50 border-t border-accent-200/50">
      <div className="px-4 md:px-6 max-w-3xl mx-auto text-center">
        <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center text-2xl text-accent-600">
          <i className="ri-information-line"></i>
        </div>
        <h4 className="text-sm font-semibold text-accent-800 mb-3">Important Disclaimer</h4>
        <p className="text-sm text-accent-700 leading-relaxed">
          This website provides informational resources only. Product availability, prices, and policies may change without notice. Always verify product details with the seller before making a purchase. We do not guarantee product availability or pricing accuracy.
        </p>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Navbar />
      <main>
        <HeroSection />
        <TrustBar />
        <CategorySection />
        <TrendingSection />
        <ArticleHub />
        <TrustSection />
        <CTASection />
        <SafetyDisclaimer />
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Kakobuy Spreadsheet — Discover Product Finds Faster',
            description: 'Your complete resource for Kakobuy spreadsheets, product categories, comparison guides and shopping tutorials.',
            url: 'https://kakobuyfinds.bond/',
            mainEntity: {
              '@type': 'ItemList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
                { '@type': 'ListItem', position: 2, name: 'Hoodies/Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
                { '@type': 'ListItem', position: 3, name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
              ],
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Kakobuy Finds',
            url: 'https://kakobuyfinds.bond/',
            logo: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
            description: 'An independent educational resource helping users understand product spreadsheets, shopping research, and online purchasing methods.',
            foundingDate: '2026',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer support',
              url: 'https://kakobuyfinds.bond/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://kakobuyfinds.bond/' },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'What is a Kakobuy Spreadsheet?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'A Kakobuy spreadsheet is a curated product database that organizes thousands of items sourced from various shopping platforms into one easily searchable document. It helps shoppers discover trending products across categories like shoes, clothing, and accessories without manually scrolling through endless listings.',
                },
              },
              {
                '@type': 'Question',
                name: 'How do I use a Kakobuy Spreadsheet to find products?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Open the spreadsheet and browse through category tabs like Shoes, Hoodies, or Accessories. Use filter and sort functions to narrow results by price or brand. Click product links to verify details on the actual product page before purchasing.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is the Kakobuy Spreadsheet free to access?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes, Kakobuy spreadsheets are community-curated resources shared freely among shoppers. They serve as educational tools for product discovery and research, helping users compare items and make informed purchasing decisions.',
                },
              },
              {
                '@type': 'Question',
                name: 'How often is the Kakobuy Spreadsheet updated?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Update frequency varies by spreadsheet. Quality spreadsheets display last-updated timestamps and are refreshed regularly to reflect current product availability, pricing changes, and newly discovered items. Always check the update date before relying on any spreadsheet data.',
                },
              },
              {
                '@type': 'Question',
                name: 'Can beginners use the Kakobuy Spreadsheet?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely. Kakobuy spreadsheets are designed to be beginner-friendly with clear category tabs, column headers, and direct product links. Start by exploring one category at a time, and use the provided guides and tutorials to learn advanced filtering and comparison techniques.',
                },
              },
            ],
          }),
        }}
      />
    </>
  );
}